(() => {
  const ROOT_SELECTOR = '[data-custom-3d-root]';
  const SCRIPT_ID = 'drbo-model-viewer-script';
  let modelViewerPromise;

  const loadModelViewer = (source) => {
    if (window.customElements?.get('model-viewer')) return Promise.resolve();
    if (modelViewerPromise) return modelViewerPromise;

    modelViewerPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'module';
      script.src = source;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return modelViewerPromise;
  };

  const init = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.custom3dReady === 'true') return;

    const slider = root.querySelector('[data-js-product-media-slider]');
    const panel = root.querySelector('[data-custom-3d-panel]');
    const viewer = panel?.querySelector('model-viewer');
    const triggers = Array.from(root.querySelectorAll('[data-custom-3d-trigger]'));
    const fullscreenButton = panel?.querySelector('[data-custom-3d-fullscreen]');
    const closeButton = panel?.querySelector('[data-custom-3d-close]');
    const status = panel?.querySelector('[data-custom-3d-status]');
    const nativeModeButtons = Array.from(root.querySelectorAll('[data-product-media-mode]'));
    const nativeModelPanel = root.querySelector('[data-product-media-model-panel]');
    const introPanel = root.querySelector('[data-product-media-intro-panel]');
    const introFrame = introPanel?.querySelector('[data-product-media-intro-frame]');

    if (!(slider instanceof HTMLElement)
      || !(panel instanceof HTMLElement)
      || !(viewer instanceof HTMLElement)
      || triggers.length === 0) return;

    root.dataset.custom3dReady = 'true';
    let sourceAttached = false;
    let lastTrigger = triggers[0];
    const underlyingControls = Array.from(root.querySelectorAll(
      '[data-js-product-media-slider] > .swiper-wrapper, .product-media__navigation, .product-media__zoom-wrapper'
    ));

    const setTriggerState = (active) => {
      triggers.forEach((trigger) => {
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-pressed', String(active));
      });
    };

    const setUnderlyingState = (disabled) => {
      underlyingControls.forEach((control) => {
        if (!(control instanceof HTMLElement)) return;
        if (disabled) {
          control.dataset.custom3dPreviousAriaHidden = control.getAttribute('aria-hidden') || '';
          control.inert = true;
          control.setAttribute('aria-hidden', 'true');
        } else {
          control.inert = false;
          const previous = control.dataset.custom3dPreviousAriaHidden;
          if (previous) control.setAttribute('aria-hidden', previous);
          else control.removeAttribute('aria-hidden');
          delete control.dataset.custom3dPreviousAriaHidden;
        }
      });
    };

    const setNativeMode = (mode) => {
      if (nativeModelPanel instanceof HTMLElement) nativeModelPanel.hidden = true;
      if (introPanel instanceof HTMLElement) introPanel.hidden = true;
      if (introFrame instanceof HTMLIFrameElement) introFrame.removeAttribute('src');
      root.dataset.productMediaActiveMode = mode;
      nativeModeButtons.forEach((button) => {
        const active = button.dataset.productMediaMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const showError = () => {
      panel.classList.remove('is-loaded');
      panel.classList.add('is-error');
      if (status) status.textContent = panel.dataset.errorLabel || '3D-Modell konnte nicht geladen werden.';
    };

    const attachSource = async () => {
      if (sourceAttached) return;
      sourceAttached = true;

      try {
        await loadModelViewer(root.dataset.modelViewerScript);
        viewer.setAttribute('src', viewer.dataset.src);
      } catch (error) {
        showError();
      }
    };

    const showViewer = (trigger) => {
      if (trigger instanceof HTMLElement) lastTrigger = trigger;
      setNativeMode('model');
      panel.hidden = false;
      root.setAttribute('data-custom-3d-active', 'true');
      setTriggerState(true);
      setUnderlyingState(true);
      attachSource();
      window.themeCore?.EventBus?.emit?.('product-slider:slide-change');
      window.requestAnimationFrame(() => viewer.focus());
    };

    const hideViewer = ({ restoreFocus = false, activatePhotos = true } = {}) => {
      if (panel.hidden) return;
      panel.hidden = true;
      root.setAttribute('data-custom-3d-active', 'false');
      setTriggerState(false);
      setUnderlyingState(false);
      if (activatePhotos) {
        const photosButton = root.querySelector('[data-product-media-mode="photos"]');
        if (photosButton instanceof HTMLElement) {
          photosButton.click();
        } else {
          setNativeMode('photos');
        }
      }
      if (restoreFocus && lastTrigger instanceof HTMLElement) lastTrigger.focus();
    };

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-custom-3d-trigger]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        showViewer(trigger);
        return;
      }

      if (event.target.closest('.product-media__slider-thumbnails-slide, .js-product-media-thumbnails-slide, [data-product-media-mode]')) {
        hideViewer({ activatePhotos: false });
      }
    }, true);

    viewer.addEventListener('progress', (event) => {
      const progress = Math.max(0, Math.min(1, Number(event.detail?.totalProgress) || 0));
      panel.style.setProperty('--drbo-3d-progress', progress);
    });

    viewer.addEventListener('load', () => {
      panel.classList.remove('is-error');
      panel.classList.add('is-loaded');
      panel.style.setProperty('--drbo-3d-progress', 1);
    });

    viewer.addEventListener('error', showError);

    closeButton?.addEventListener('click', () => hideViewer({ restoreFocus: true }));

    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      hideViewer({ restoreFocus: true });
    });

    root.querySelectorAll('.product-media__navigation-button, [data-js-zoom-button]').forEach((control) => {
      control.addEventListener('click', () => hideViewer({ activatePhotos: false }));
    });

    slider.addEventListener('touchstart', (event) => {
      if (!event.target.closest('[data-custom-3d-panel]')) hideViewer();
    }, { passive: true });

    const section = root.closest('[data-section-type="product"]');
    if (section?.dataset.sectionId && window.themeCore?.EventBus?.listen) {
      window.themeCore.EventBus.listen(`pdp:section-${section.dataset.sectionId}:change-variant`, hideViewer);
    }

    fullscreenButton?.addEventListener('click', async () => {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else {
          await panel.requestFullscreen();
        }
      } catch (error) {
        // Fullscreen can be unavailable in embedded theme previews.
      }
    });

    document.addEventListener('fullscreenchange', () => {
      if (!(fullscreenButton instanceof HTMLElement)) return;
      fullscreenButton.setAttribute('aria-pressed', String(document.fullscreenElement === panel));
    });

  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
